import { useState } from "react"
import { MoreHorizontal, Pencil, FolderInput, Trash2, Star, StarOff } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { FolderPicker } from "@/components/capture/FolderPicker"

interface FolderContextMenuProps {
  path: string
  name: string
  rootFolder: string
  onRename: (path: string, newName: string) => void
  onDelete: (path: string) => void
  onMove: (fromPath: string, toPath: string) => void
  isRenaming?: boolean
  onStartRename?: () => void
  onStopRename?: () => void
  isFavorite?: boolean
  onToggleFavorite?: (path: string, name: string) => void
}

export function FolderContextMenu({ path, name, rootFolder, onRename, onDelete, onMove, isRenaming, onStartRename, onStopRename, isFavorite, onToggleFavorite }: FolderContextMenuProps) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showFolderPicker, setShowFolderPicker] = useState(false)

  function handleDelete() {
    onDelete(path)
    setShowDeleteConfirm(false)
  }

  function handleMove(destination: string) {
    const folderName = path.split("/").pop()
    onMove(path, `${destination}/${folderName}`)
    setShowFolderPicker(false)
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="rounded-md p-1 hover:bg-accent"
            data-testid={`folder-context-menu-${name}`}
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {onToggleFavorite && (
            <DropdownMenuItem onClick={() => onToggleFavorite(path, name)} className="gap-2">
              {isFavorite ? <StarOff className="h-4 w-4" /> : <Star className="h-4 w-4" />}
              {isFavorite ? "Remove from Favorites" : "Add to Favorites"}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => onStartRename?.()} className="gap-2">
            <Pencil className="h-4 w-4" />
            Rename
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setShowFolderPicker(true)} className="gap-2">
            <FolderInput className="h-4 w-4" />
            Move
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => setShowDeleteConfirm(true)}
            className="gap-2 text-destructive"
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete folder</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &quot;{name}&quot; and all its contents? This will remove it from Dropbox.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-folder-delete">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <FolderPicker
        open={showFolderPicker}
        onClose={() => setShowFolderPicker(false)}
        onSelect={handleMove}
        rootPath={rootFolder}
      />
    </>
  )
}
